import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@store/store-helper";
import {
  PublishedData,
  PublishedElsData,
} from "@pages/data-management/published-data/published-data-types";
import { allCaptureTreeRevisionsSelector } from "@store/capture-tree/capture-tree-selectors";
import { selectAllIElementsOfType } from "@faro-lotv/project-source";
import { isIElementElsDataSet } from "@custom-types/i-elements-type-guards";
import { CaptureApiClient, RegistrationState } from "@faro-lotv/service-wires";
import {
  checkIsPublishing,
  checkIsPublished,
  hasPublishError,
} from "@pages/data-management/published-data/published-data-utils";
import { latestUpload } from "@pages/data-management/uploaded-data/uploaded-data-utils";
import { hasAnyDraftChangesSelector } from "@store/capture-tree/capture-tree-selectors";
import { processScanTasksSelector } from "@pages/data-management/uploaded-data/uploaded-data-selectors";
import { registeredDataSelector } from "@pages/data-management/registered-data/registered-data-selectors";
import { sdbBackgroundTasksSelector } from "@store/sdb-background-tasks/sdb-background-tasks-selector";
import { isProjectPointCloudCreatorTask } from "@custom-types/sdb-background-tasks-type-guards";

/**
 * @returns The photogrammetry scan published from a registration of photogrammetry scans.
 * The published photogrammetry scan corresponds to the last revision that was merged by the registration backend and
 * the associated IElementPhotogrammetryScan entity.
 */
const publishedPhotogrammetryDataSelector: (
  state: RootState
) => PublishedData | undefined = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    const revisions = allCaptureTreeRevisionsSelector(state);
    const filteredRevisions = revisions.filter((revision) => {
      return (
        revision.state === RegistrationState.merged &&
        revision.targetRevisionId === null &&
        revision.createdByClient === CaptureApiClient.photogrammetry
      );
    });

    if (!filteredRevisions.length) {
      return;
    }

    const lastRevision = filteredRevisions.reduce((last, current) => {
      return new Date(current.createdAt) > new Date(last.createdAt)
        ? current
        : last;
    }, filteredRevisions[0]);

    return {
      ...lastRevision,
    };
  }
);

/**
 * @returns The ELS dataset published from a registration of ELS scans.
 * The published ELS dataset corresponds to the last revision that was merged by the registration backend and
 * the associated IElementElsDataSet entity.
 */
const publishedElsDataSelector: (
  state: RootState
) => PublishedElsData | undefined = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    const revisions = allCaptureTreeRevisionsSelector(state);
    const filteredRevisions = revisions.filter((revision) => {
      return revision.state !== RegistrationState.canceled && (
        revision.createdByClient === CaptureApiClient.projectPointCloudCreator ||
        revision.createdByClient === CaptureApiClient.registrationBackend ||
        // Previously, we considered all SCENE revisions, even open or canceled ones. This seemed incorrect.
        (revision.createdByClient === CaptureApiClient.scene && revision.state === RegistrationState.merged)
      );
    });

    if (!filteredRevisions.length) {
      return;
    }

    const lastRevision = filteredRevisions.reduce((last, current) => {
      return new Date(current.createdAt) > new Date(last.createdAt)
        ? current
        : last;
    }, filteredRevisions[0]);

    const elsDataSets = selectAllIElementsOfType(isIElementElsDataSet)(state);

    return {
      ...lastRevision,
      element: elsDataSets.length ? elsDataSets[0] : undefined,
    };
  }
);

/**
 * Selector to get all published ELS and Photogrammetry revisions.
 * Hint: You may want to use `checkIsPublished` with this selector to determine if
 * the latest state is published, i.e. the data is published and has no draft changes.
 * @returns All PublishedData revisions.
 */
export const publishedDataSelector: (state: RootState) => PublishedData[] =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const publishedElsData = publishedElsDataSelector(state);
      const publishedPhotogrammetryData = publishedPhotogrammetryDataSelector(state);

      // For now we only return the published ELS and Photogrammetry data.
      const publishedRevisions = [publishedElsData, publishedPhotogrammetryData].filter(
        (data) => data !== undefined
      );

      // Stick PPC task to the published revisions.
      const backgroundTasks = sdbBackgroundTasksSelector(state);
      const projectPointCloudCreatorTasks = backgroundTasks.filter(isProjectPointCloudCreatorTask);
      // The PPC tasks currently don't have direct link to any revision except that we know
      // it may be created by RegistrationBackend or ProjectPointCloudCreator.
      // The RegistrationBackend revision is only transitionary and soon won't be used,
      // so we should take the most recent one after the PPC revision was created.
      const publishedRevisionsWithTask: PublishedData[] = publishedRevisions.map((revision) => {
        // Early out if the revision is not coming from ProjectPointCloudCreator.
        if (revision.createdByClient !== CaptureApiClient.projectPointCloudCreator &&
          // Special case: Since there is no PPC revision yet when the task is created/scheduled,
          // we have to assume that this case is still valid and allow the task to be assigned to
          // the latest successful RegistrationBackend revision.
          (revision.createdByClient !== CaptureApiClient.registrationBackend && revision.state !== RegistrationState.merged)) {
          return revision;
        }
        const task = projectPointCloudCreatorTasks.find(
          (ppcTask) =>
            ppcTask.createdAt >= revision.createdAt
        );
        return {
          ...revision,
          task,
        };
      });

      return publishedRevisionsWithTask;
    }
  );

/**
 * @returns true if there are any revisions that are currently being published.
 */
export const isPublishingSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const revisions = publishedDataSelector(state);
      return 0 < revisions.length && checkIsPublishing(revisions[0]);
    }
  );

/**
 * @returns true if the data is published, and has no draft changes or ongoing tasks.
 *          Also checks that the time of publish is after upload and registration.
 */
export const isPublishedSelector: (state: RootState) => boolean =
createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    const revisions = publishedDataSelector(state);
    if (revisions.length === 0) {
      return false;
    }

    // To properly check if the data is published, we need to check the published data
    // against the latest upload and registration time.
    const hasAnyDraftChanges = hasAnyDraftChangesSelector(state);
    const processScanTasks = processScanTasksSelector(state);
    const timeApproxUpload = latestUpload(processScanTasks);
    const registrationRevisions = registeredDataSelector(state);
    const registrationRevision = registrationRevisions?.[0];
    const timeRegister = registrationRevision ? (new Date(registrationRevision.modifiedAt)).getTime() : 0;
    const backgroundTasks = sdbBackgroundTasksSelector(state);

    return 0 < revisions.length &&
      checkIsPublished(revisions, hasAnyDraftChanges, timeApproxUpload, timeRegister, backgroundTasks);
  }
);

/**
 * @returns true if the PublishedData revision has a task that failed.
 */
export const hasPublishErrorSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const revisions = publishedDataSelector(state);
      const hasRevisions = revisions.length > 0;
      return hasRevisions && hasPublishError(revisions[0]);
    }
  );
